import {
  Button,
  Checkbox,
  Flex,
  Icon,
  RadioGroup,
  Slider,
  Text,
  Tooltip,
} from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { CounterType } from "definitions/counter-api";
import { PaymentSubscribeRequestDto } from "definitions/poizon-bff";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { DewuLogo, GoatLogo, PoizonComLogo } from "src/components";
import { subscribeCheckoutAction } from "src/store/fetchers/subscribeCheckout";

import { calculateMaxRequests, numberToCurrencyNumber } from "../../util";
import { FormParamCard } from "./components";
import { SUBSCRIBE_DURATIONS } from "./SubscribeFormSection.constants";
import css from "./SubscribeFormSection.module.scss";

interface SubscribeFormSectionProps {
  price: number;
  isLoadingPrice: boolean;
}

export const SubscribeFormSection = ({
  price,
  isLoadingPrice,
}: SubscribeFormSectionProps) => {
  const { control, watch, handleSubmit, setValue } =
    useFormContext<PaymentSubscribeRequestDto>();
  const rps = watch("rps");

  const subscribeCheckout = useAction(subscribeCheckoutAction);

  useEffect(() => {
    setValue("type", CounterType.AllSourcesRequests);
  }, [setValue]);

  const [isLoading] = useAtom(subscribeCheckoutAction.isLoadingAtom);

  const onSubmit = handleSubmit((data) => {
    subscribeCheckout({ data });
  });

  const maxRequests = calculateMaxRequests(rps);

  return (
    <section className={css.subscribeFormSectionWrapper}>
      <div className={css.subscribeFormCard}>
        <form style={{ width: "100%" }} onSubmit={onSubmit}>
          <Text as={"div"} style={{ marginBottom: 10 }} variant="header-1">
            Конфигурация
          </Text>
          <FormParamCard
            title="Платформа"
            value={
              <Flex alignItems="center" gap={3}>
                <div className={css.stackedIcons}>
                  <div className={css.stackedIcon} style={{ zIndex: 3 }}>
                    <Icon data={DewuLogo} size={22} />
                  </div>
                  <div className={css.stackedIcon} style={{ zIndex: 2 }}>
                    <Icon data={PoizonComLogo} size={22} />
                  </div>
                  <div className={css.stackedIcon} style={{ zIndex: 1 }}>
                    <Icon data={GoatLogo} size={22} />
                  </div>
                </div>
                Все платформы
              </Flex>
            }
          />
          <Controller
            name="rps"
            control={control}
            render={({ field }) => (
              <FormParamCard
                title="Запросов в секунду (RPS)"
                value={field.value + ""}
                endContent={
                  <Slider
                    tooltipDisplay="off"
                    min={0.5}
                    max={5}
                    step={0.5}
                    value={field.value}
                    onUpdate={field.onChange}
                    // onUpdate={handleOnUpdateSliderRps}
                  />
                }
              />
            )}
          />
          <Controller
            name="req"
            control={control}
            render={({ field }) => (
              <FormParamCard
                title="Кол-во запросов"
                value={
                  field.value === maxRequests
                    ? "Безлимит"
                    : field.value?.toLocaleString("ru-RU")
                }
                endContent={
                  <Slider
                    tooltipDisplay="off"
                    value={field.value}
                    onUpdate={field.onChange}
                    min={10000}
                    max={maxRequests}
                    markFormat={(value) => {
                      return value === maxRequests
                        ? "Безлимит"
                        : value.toLocaleString("ru-RU");
                    }}
                    step={10000}
                  />
                }
              />
            )}
          />
          <Controller
            name="time"
            control={control}
            render={() => (
              <FormParamCard
                title="Длительность подписки"
                value={
                  <RadioGroup
                    size="l"
                    defaultValue={SUBSCRIBE_DURATIONS[0]?.value}
                    options={SUBSCRIBE_DURATIONS}
                  />
                }
              />
            )}
          />
          <FormParamCard
            title="Опции"
            value={
              <>
                <Controller
                  name="withExport"
                  control={control}
                  disabled={rps < 1}
                  render={({ field }) => (
                    <Flex gap={2} alignItems={"center"}>
                      <Tooltip
                        content={"Добавляет в подписку доступ к выгрузкам"}
                      >
                        <Checkbox
                          content="Включить выгрузки"
                          checked={field.value}
                          onUpdate={field.onChange}
                          disabled={field.disabled}
                        />
                      </Tooltip>
                    </Flex>
                  )}
                />
              </>
            }
          />
          <br />
          <Flex direction="column" gap={4}>
            <Flex alignItems="center" justifyContent="space-between">
              <Text variant="body-3">Итого:</Text>
              {isLoadingPrice ? (
                <Skeleton
                  className={css.skeletonCard}
                  style={{ height: 26, width: 150 }}
                />
              ) : (
                <Text variant="header-2">{numberToCurrencyNumber(price)}</Text>
              )}
            </Flex>

            <Button
              type="submit"
              view="action"
              size="xl"
              width="max"
              loading={isLoading}
            >
              <Text variant="subheader-3" color="light-primary">
                Оформить подписку
              </Text>
            </Button>
          </Flex>
        </form>
      </div>
    </section>
  );
};
